import {
  Mic,
  Zap,
  ShieldCheck,
  PhoneCall,
  Bot,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Activity,
  Cpu,
  RefreshCw,
} from "lucide-react";
import { Section, SectionHeading, Card } from "./service-revops-ui";
import { SectionCta } from "./service-revops-section-cta";

/**
 * Voice AI / Vapi exclusive:
 * 1. Sub-300ms Conversational Latency & Turn-Taking Benchmark
 * 2. Production Voice AI Guardrail & Human Handoff Stack
 * Self-contained, reverse-engineered from top Voice AI platforms & consultancies
 * (Retell, Bland, Synthflow, Alice Labs, Cekura).
 */

const benchmarks = [
  {
    icon: Zap,
    label: "Conversational Latency",
    value: "<300ms",
    desc: "Sub-second speech-to-speech roundtrip. Natural turn-taking without awkward pauses or robotic delays.",
  },
  {
    icon: Activity,
    label: "Workflow Accuracy",
    value: "99.4%",
    desc: "Strict state-machine execution ensuring the agent never goes off-script or invents unverified pricing.",
  },
  {
    icon: PhoneCall,
    label: "Speed-to-Lead Response",
    value: "<15s",
    desc: "Inbound call pick-up or instant outbound dial on webform fill before the prospect reaches a competitor.",
  },
];

const guardrailSteps = [
  {
    icon: ShieldCheck,
    step: "01",
    title: "Dynamic Knowledge Grounding",
    desc: "Strict RAG boundary prevents hallucination. The agent answers only from verified company knowledge bases and explicitly declines unknown queries.",
  },
  {
    icon: RefreshCw,
    step: "02",
    title: "Mid-Sentence Interruption & Turn-Taking",
    desc: "Proprietary turn-taking logic senses when a prospect interrupts, immediately stopping speech to listen without cutting off important context.",
  },
  {
    icon: Bot,
    step: "03",
    title: "Warm Human Escalation",
    desc: "When sentiment, deal size, or complex objections exceed threshold, the agent briefs the human rep and seamlessly transfers the live call.",
  },
  {
    icon: Cpu,
    step: "04",
    title: "Instant Webhook CRM Telemetry",
    desc: "Full transcript, structured LLM summary, objection tags, and sentiment score pushed to your CRM the moment the call terminates.",
  },
];

const latencyComparison = [
  {
    label: "Response Speed",
    bad: "1,200ms+ robotic delay",
    good: "<300ms natural human pace",
  },
  {
    label: "Objection Handling",
    bad: "Static linear prompt scripts",
    good: "Dynamic LLM context reasoning",
  },
  {
    label: "CRM Data Logging",
    bad: "Manual rep notes or lost calls",
    good: "100% automated webhook sync",
  },
  {
    label: "Human Escalation",
    bad: "Cold drop to voicemail",
    good: "Warm transfer with live rep briefing",
  },
];

export function ServiceVoiceAiComparison() {
  return (
    <>
      {/* §3b.1 — Latency & Speed Benchmark */}
      <Section>
        <SectionHeading
          eyebrow="THE LATENCY & ACCURACY BENCHMARK"
          navy="Conversational Speed Is the"
          blue="Difference Between Conversion & Hang-Up."
          sub="Un-architected voice bots fail because callers hate awkward 2-second pauses. I engineer sub-300ms pipeline latency and strict state guardrails so calls feel like talking to your top sales rep."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {benchmarks.map((b) => {
            const Icon = b.icon;
            return (
              <Card key={b.label} radius={24}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #0B7CF2, #0860BE)",
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span
                      className="font-sans font-bold uppercase tracking-[0.14em]"
                      style={{ fontSize: "11px", color: "#0B7CF2" }}
                    >
                      {b.label}
                    </span>
                  </div>
                </div>
                <div
                  className="font-display font-black mb-2"
                  style={{
                    fontSize: "clamp(36px, 5vw, 52px)",
                    lineHeight: 1,
                    letterSpacing: "-0.04em",
                    color: "#071E61",
                  }}
                >
                  {b.value}
                </div>
                <p
                  style={{
                    fontSize: "14.5px",
                    lineHeight: 1.55,
                    color: "#536B89",
                  }}
                >
                  {b.desc}
                </p>
              </Card>
            );
          })}
        </div>

        {/* Before / after table */}
        <Card>
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5" style={{ color: "#0B7CF2" }} />
            <span
              className="font-sans font-bold uppercase tracking-[0.14em]"
              style={{ fontSize: "12px", color: "#0B7CF2" }}
            >
              Un-Architected Prompt Bot vs Engineered Vapi Stack
            </span>
          </div>
          <div className="space-y-1">
            {latencyComparison.map((r) => (
              <div
                key={r.label}
                className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3 md:gap-4 py-3.5 border-b border-[rgba(8,46,103,0.06)] last:border-0"
              >
                <span
                  className="font-display font-bold"
                  style={{ fontSize: "15px", color: "#071E61" }}
                >
                  {r.label}
                </span>
                <AlertTriangle
                  className="hidden md:block w-4 h-4 shrink-0"
                  style={{ color: "#D9534F" }}
                />
                <span style={{ fontSize: "14px", color: "#8C5A57" }}>
                  {r.bad}
                </span>
                <ArrowRight
                  className="hidden md:block w-4 h-4 shrink-0"
                  style={{ color: "#0B7CF2" }}
                />
                <span
                  className="flex items-center gap-2 font-medium"
                  style={{ fontSize: "14px", color: "#169846" }}
                >
                  <CheckCircle2
                    className="w-4 h-4 shrink-0"
                    style={{ color: "#27DC5D" }}
                  />
                  {r.good}
                </span>
              </div>
            ))}
          </div>
        </Card>
        <SectionCta
          copy="Want human-standard sub-300ms voice agents? Let's audit your Vapi stack."
          label="Architect My Voice Agent"
        />
      </Section>

      {/* §3b.2 — Production Voice AI Guardrail Stack */}
      <Section>
        <SectionHeading
          eyebrow="PRODUCTION GOVERNANCE"
          navy="Zero Hallucination."
          blue="Four-Tier Voice AI Guardrail Architecture."
          sub="Prompts alone cannot guarantee enterprise brand safety. I engineer four layers of deterministic guardrails, fallbacks, and human escalation into every Vapi deployment."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {guardrailSteps.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.title} radius={24}>
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #0B7CF2, #0860BE)",
                    }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span
                    className="font-display font-black select-none"
                    style={{
                      fontSize: "44px",
                      lineHeight: 1,
                      color: "rgba(17,143,247,0.16)",
                      letterSpacing: "-0.05em",
                    }}
                  >
                    {s.step}
                  </span>
                </div>
                <h3
                  className="font-display font-bold mb-2"
                  style={{
                    fontSize: "20px",
                    color: "#071E61",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.title}
                </h3>
                <p
                  style={{
                    fontSize: "14.5px",
                    lineHeight: 1.55,
                    color: "#536B89",
                  }}
                >
                  {s.desc}
                </p>
              </Card>
            );
          })}
        </div>
        <SectionCta
          copy="Deploying Voice AI? I'll build the guardrails that keep your brand safe."
          label="Engineer My Voice Guardrails"
        />
      </Section>
    </>
  );
}
